// 「切换节点」资源 CDN 节点 hook:从 v_resource_node_public(PostgREST 直查,anon 可读)
// 取真实资源域名,并驱动真人测速 + 回传。设计 §2/§3/§8。
import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { CdnNode } from '../components/modals/node-test-modal'
import { fetchResourceNodes } from './resource-node-auto'
import { measureResourceDomain, reportResourceSpeed, generateBeaconPoW } from './resource-speedtest'

// 首个虚拟「自动」节点:不对应具体域名,不参与测速。
const AUTO_NODE: CdnNode = { id: 'auto', name: '自动(推荐)', region: '智能选路', latency: null }

/**
 * 拉取公开资源节点(auto + v_resource_node_public 行)。
 * name 取 region(回退 domain),domain 供测速归因,latency 初始为空。
 */
export function useResourceNodes() {
  const query = useQuery({
    queryKey: ['resource-nodes-public'] as const,
    queryFn: async (): Promise<CdnNode[]> => {
      const rows = await fetchResourceNodes()
      return rows.map((r) => ({
        id: r.id,
        name: r.region || r.domain,
        region: r.domain,
        domain: r.domain,
        latency: null,
      }))
    },
    staleTime: 5 * 60_000,
  })

  const baseNodes = query.data ?? []

  // 测速结果覆盖:node id → latency(ms)。
  const [latencies, setLatencies] = useState<Record<string, number | null>>({})
  const [pinging, setPinging] = useState(false)

  const nodes: CdnNode[] = [
    AUTO_NODE,
    ...baseNodes.map((n) => ({ ...n, latency: latencies[n.id] ?? n.latency ?? null })),
  ]

  const pingAll = useCallback(() => {
    const targets = baseNodes.filter((n) => !!n.domain)
    if (targets.length === 0) return
    setPinging(true)
    // 一次生成 PoW,本轮所有回传复用(PoW 无状态;js-challenge 未载 → null → 不回传)。
    const pow = generateBeaconPoW()
    void Promise.all(
      targets.map(async (n) => {
        const r = await measureResourceDomain(n.domain as string)
        // fire-and-forget 回传(kind=resource;需带 PoW,后端校验)。
        reportResourceSpeed(r.domain, r.ms, r.ok, pow)
        setLatencies((prev) => ({ ...prev, [n.id]: r.ok && isFinite(r.ms) ? Math.round(r.ms) : null }))
      }),
    ).finally(() => setPinging(false))
  }, [baseNodes])

  return { nodes, pinging, pingAll }
}
